import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  Slide,
  Fade,
  Typography,
  useMediaQuery,
  CircularProgress,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import WorkOutlineRoundedIcon from '@mui/icons-material/WorkOutlineRounded';
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import webPushService from '../../utils/webPush';

/**
 * Premium, trust-first notification opt-in.
 *
 * Design principles:
 * - Soft "pre-prompt": we ask kindly in-app BEFORE touching the native browser
 *   dialog, so we never burn the one-shot OS prompt and never feel pushy.
 * - "Later" is a first-class, guilt-free choice with a warm acknowledgement.
 * - The native permission dialog only ever fires from the primary tap (a real
 *   user gesture), which is exactly what iOS/Safari require and what keeps
 *   Chrome from demoting us to its silent "quiet" UI.
 */

// ---------------------------------------------------------------------------
// COPY VARIATIONS — 5 polished, production-ready sets.
// Swap `DEFAULT_VARIATION` (or pass the `variant` prop) to A/B test. None use
// pressure words ("Allow notifications", "Enable now", "Don't miss out", ...).
// ---------------------------------------------------------------------------
export const VARIATIONS = [
  {
    key: 'in-the-loop',
    Icon: NotificationsNoneRoundedIcon,
    title: 'Get important updates',
    description: 'Allow ULCAAPP to send you notifications about your bookings, work requests, reminders, and other important updates.',
    primaryLabel: 'Allow Notifications',
    laterLabel: 'Maybe Later',
    laterTitle: "You're all set",
    laterMessage: 'All good either way. You can turn these on anytime from your profile.',
  },
  {
    key: 'theres-news',
    Icon: ChatBubbleOutlineRoundedIcon,
    title: "Know when there's news",
    description: "We'll gently nudge you when someone replies or a booking updates.",
    primaryLabel: 'Sounds good',
    laterLabel: 'Not now',
    laterTitle: 'No worries at all',
    laterMessage: "Everything keeps working just fine — we're here whenever you're ready.",
  },
  {
    key: 'new-work',
    Icon: WorkOutlineRoundedIcon,
    title: 'New work, straight to you',
    description: 'Hear about matching jobs the moment they’re posted near you.',
    primaryLabel: 'Keep me updated',
    laterLabel: 'Maybe later',
    laterTitle: 'Totally fine',
    laterMessage: 'You can still browse jobs anytime. Switch this on whenever it suits you.',
  },
  {
    key: 'gentle',
    Icon: FavoriteBorderRoundedIcon,
    title: 'A gentle heads-up',
    description: 'The updates that matter, like bookings and replies.',
    primaryLabel: 'That works for me',
    laterLabel: 'Not right now',
    laterTitle: 'Perfectly fine',
    laterMessage: "You're in control — turn these on anytime from settings.",
  },
  {
    key: 'minimal',
    Icon: AutoAwesomeRoundedIcon,
    title: 'Stay updated',
    description: 'Timely updates about your bookings and messages.',
    primaryLabel: 'Continue',
    laterLabel: 'Later',
    laterTitle: 'You’re all set',
    laterMessage: 'You can turn these on whenever you like.',
  },
];

const DEFAULT_VARIATION = 0;

const SNOOZE_KEY = 'ulcaapp:notif:softPromptSnoozedAt';
const DONE_KEY = 'ulcaapp:notif:softPromptDone';
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000; // give "Later" a full week of breathing room
const SHOW_DELAY_MS = 4000; // let the user settle in before asking
const LATER_AUTOCLOSE_MS = 3000; // warm acknowledgement lingers ~3s, then fades out

const SlideUp = React.forwardRef(function SlideUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const readSnoozedAt = () => {
  try {
    return parseInt(localStorage.getItem(SNOOZE_KEY) || '0', 10) || 0;
  } catch (_) {
    return 0;
  }
};

const isEligibleNow = () => {
  if (process.env.NODE_ENV === 'development') return false;
  if (!webPushService.isPushSupported()) return false;
  if (webPushService.getPermissionState() !== 'default') return false; // already granted/denied
  try {
    if (localStorage.getItem(DONE_KEY) === 'true') return false;
  } catch (_) {}
  if (Date.now() - readSnoozedAt() < SNOOZE_MS) return false;
  return true;
};

const NotificationOptIn = ({ variant = DEFAULT_VARIATION }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const { user } = useAuth();

  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState('ask'); // 'ask' | 'later'
  const [busy, setBusy] = useState(false);
  const primaryRef = useRef(null);
  const laterRef = useRef(null);

  const copy = useMemo(() => VARIATIONS[variant] || VARIATIONS[DEFAULT_VARIATION], [variant]);
  const Icon = copy.Icon;

  const titleId = 'notif-optin-title';
  const descId = 'notif-optin-desc';

  // Decide whether/when to surface the sheet once the user is authenticated.
  useEffect(() => {
    if (!user) return undefined;
    if (!isEligibleNow()) return undefined;

    const t = setTimeout(() => {
      // Re-check right before showing (permission may have changed elsewhere).
      if (isEligibleNow()) {
        setPhase('ask');
        setOpen(true);
      }
    }, SHOW_DELAY_MS);

    return () => clearTimeout(t);
  }, [user]);

  const snooze = useCallback(() => {
    try {
      localStorage.setItem(SNOOZE_KEY, Date.now().toString());
    } catch (_) {}
  }, []);

  const markDone = useCallback(() => {
    try {
      localStorage.setItem(DONE_KEY, 'true');
    } catch (_) {}
  }, []);

  const close = useCallback(() => setOpen(false), []);

  const handleEnable = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await webPushService.enableNotificationsFromUserGesture();
      if (res?.ok) {
        markDone();
        setOpen(false);
        toast.success("You're all set — we'll keep you posted.");
      } else if (res?.reason === 'denied') {
        // The browser is now blocking; don't nag. Remember and step aside warmly.
        markDone();
        setOpen(false);
        toast.info('No problem — you can turn these on anytime from your browser settings.');
      } else {
        // Dismissed the native dialog, or a transient failure. Treat as "Later".
        snooze();
        setOpen(false);
      }
    } catch (_) {
      snooze();
      setOpen(false);
    } finally {
      setBusy(false);
    }
  }, [busy, markDone, snooze]);

  const handleLater = useCallback(() => {
    if (busy) return;
    snooze();
    setPhase('later');
  }, [busy, snooze]);

  // Auto-dismiss the warm "later" acknowledgement, and move focus to it so
  // screen-reader + keyboard users are not left on a button that just unmounted.
  useEffect(() => {
    if (phase !== 'later' || !open) return undefined;
    if (laterRef.current) laterRef.current.focus();
    const t = setTimeout(() => setOpen(false), LATER_AUTOCLOSE_MS);
    return () => clearTimeout(t);
  }, [phase, open]);

  const accent = theme.palette.primary.main;
  // Slide up when asking; gently fade the "Later" acknowledgement out.
  const TransitionComponent = prefersReducedMotion
    ? undefined
    : phase === 'later'
    ? Fade
    : SlideUp;

  return (
    <Dialog
      open={open}
      onClose={phase === 'ask' ? handleLater : close}
      TransitionComponent={TransitionComponent}
      transitionDuration={prefersReducedMotion ? 0 : { enter: 320, exit: 280 }}
      keepMounted={false}
      aria-labelledby={titleId}
      aria-describedby={phase === 'ask' ? descId : undefined}
      sx={{
        '& .MuiDialog-container': {
          alignItems: { xs: 'flex-end', sm: 'center' },
        },
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(15, 15, 15, 0.45)',
          backdropFilter: 'blur(2px)',
        },
      }}
      PaperProps={{
        elevation: 0,
        sx: {
          m: { xs: 0, sm: 2 },
          width: '100%',
          maxWidth: { sm: 400 },
          borderRadius: { xs: '24px 24px 0 0', sm: '24px' },
          px: { xs: 2.5, sm: 3 },
          pt: { xs: 2, sm: 3 },
          pb: `calc(env(safe-area-inset-bottom, 0px) + ${isMobile ? '20px' : '24px'})`,
          background: 'linear-gradient(180deg, #ffffff 0%, #fbfbfd 100%)',
          overflow: 'hidden',
        },
      }}
    >
      {/* Mobile grabber for a native bottom-sheet feel */}
      {isMobile && (
        <Box
          aria-hidden
          sx={{
            width: 40,
            height: 4,
            borderRadius: 999,
            bgcolor: 'rgba(0,0,0,0.12)',
            mx: 'auto',
            mb: 2,
          }}
        />
      )}

      {phase === 'ask' ? (
        <Box sx={{ textAlign: 'center' }}>
          <Box
            aria-hidden
            sx={{
              width: 64,
              height: 64,
              mx: 'auto',
              mb: 2,
              borderRadius: '20px',
              display: 'grid',
              placeItems: 'center',
              color: accent,
              background: `linear-gradient(135deg, ${alpha(accent, 0.16)} 0%, ${alpha(accent, 0.06)} 100%)`,
              boxShadow: `inset 0 0 0 1px ${alpha(accent, 0.12)}`,
            }}
          >
            <Icon sx={{ fontSize: 32 }} />
          </Box>

          <Typography
            id={titleId}
            component="h2"
            sx={{ fontSize: '1.2rem', fontWeight: 700, color: '#1c1c1e', letterSpacing: '-0.01em' }}
          >
            {copy.title}
          </Typography>

          <Typography
            id={descId}
            sx={{
              mt: 0.75,
              mb: 2.75,
              fontSize: '0.95rem',
              lineHeight: 1.5,
              color: 'rgba(28,28,30,0.72)',
              maxWidth: 320,
              mx: 'auto',
            }}
          >
            {copy.description}
          </Typography>

          <Button
            ref={primaryRef}
            autoFocus
            fullWidth
            disableElevation
            variant="contained"
            onClick={handleEnable}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem',
              borderRadius: 999,
              py: 1.25,
              background: `linear-gradient(135deg, ${accent} 0%, ${theme.palette.primary.dark} 100%)`,
              '&:hover': { filter: 'brightness(1.03)' },
            }}
          >
            {busy ? 'One moment…' : copy.primaryLabel}
          </Button>

          <Button
            fullWidth
            onClick={handleLater}
            disabled={busy}
            sx={{
              mt: 1,
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.95rem',
              borderRadius: 999,
              py: 1,
              color: 'rgba(28,28,30,0.66)',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)' },
            }}
          >
            {copy.laterLabel}
          </Button>
        </Box>
      ) : (
        <Box
          ref={laterRef}
          tabIndex={-1}
          role="status"
          aria-live="polite"
          sx={{ textAlign: 'center', py: 1, outline: 'none' }}
        >
          <Box
            aria-hidden
            sx={{
              width: 64,
              height: 64,
              mx: 'auto',
              mb: 2,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              color: theme.palette.success.main,
              background: alpha(theme.palette.success.main, 0.12),
            }}
          >
            <CheckCircleRoundedIcon sx={{ fontSize: 34 }} />
          </Box>
          <Typography
            id={titleId}
            component="h2"
            sx={{ fontSize: '1.15rem', fontWeight: 700, color: '#1c1c1e' }}
          >
            {copy.laterTitle}
          </Typography>
          <Typography
            sx={{
              mt: 0.75,
              fontSize: '0.95rem',
              lineHeight: 1.5,
              color: 'rgba(28,28,30,0.72)',
              maxWidth: 320,
              mx: 'auto',
            }}
          >
            {copy.laterMessage}
          </Typography>
        </Box>
      )}
    </Dialog>
  );
};

export default NotificationOptIn;
